const CLIENT_TOKEN_KEY = "ATk";
const CLIENT_TOKEN_EXP_KEY = "ATk_exp";
const USER_ACCOUNT_KEY = "AccountInfo";
const TOKEN_SKEW_MS = 60000;
const GRAPHQL_NOT_AUTHORISED_RE = /not authorised|not authorized|unauthorized/i;

export function stripBearer(token) {
  if (!token || typeof token !== "string") return "";
  return token.replace(/^Bearer\s+/i, "").trim();
}

function decodeBase64Url(segment) {
  const paddedBase64 = segment.replace(/-/g, "+").replace(/_/g, "/");
  const padLength = paddedBase64.length % 4;
  const withPad = padLength ? paddedBase64 + "=".repeat(4 - padLength) : paddedBase64;
  const decoded = atob(withPad);
  try {
    return decodeURIComponent(
      decoded
        .split("")
        .map((char) => "%" + ("00" + char.charCodeAt(0).toString(16)).slice(-2))
        .join("")
    );
  } catch (err) {
    return decoded;
  }
}

export function parseJwtPayload(token) {
  const raw = stripBearer(token);
  const parts = raw.split(".");
  if (parts.length < 2) return null;
  try {
    const payload = JSON.parse(decodeBase64Url(parts[1]));
    return payload && typeof payload === "object" ? payload : null;
  } catch (err) {
    return null;
  }
}

export function getJwtExpirationDate(token) {
  const payload = parseJwtPayload(token);
  if (!payload || payload.exp == null) return null;
  const expMs = Number(payload.exp) * 1000;
  if (!Number.isFinite(expMs)) return null;
  const date = new Date(expMs);
  return Number.isNaN(date.getTime()) ? null : date;
}

export function parseStoredExpiration(value) {
  if (value == null || value === "") return null;
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? null : date;
}

export function isExpiredDate(date, skewMs = TOKEN_SKEW_MS, nowMs = Date.now()) {
  if (!date || Number.isNaN(date.getTime())) return true;
  return nowMs >= date.getTime() - skewMs;
}

export function isAccessTokenExpired(token, storedExpValue, skewMs = TOKEN_SKEW_MS, nowMs = Date.now()) {
  if (!token) return true;
  const jwtExp = getJwtExpirationDate(token);
  if (jwtExp) return isExpiredDate(jwtExp, skewMs, nowMs);
  const storedExp = parseStoredExpiration(storedExpValue);
  if (storedExp) return isExpiredDate(storedExp, skewMs, nowMs);
  return true;
}

export function readUserAccessToken(storage = localStorage) {
  try {
    const raw = storage.getItem(USER_ACCOUNT_KEY);
    if (!raw) return "";
    const user = JSON.parse(raw);
    return user && typeof user.accesstoken === "string" ? user.accesstoken : "";
  } catch (err) {
    return "";
  }
}

export function hasUserSession(storage = localStorage) {
  return Boolean(readUserAccessToken(storage));
}

export function isUserSessionExpired(storage = localStorage, skewMs = TOKEN_SKEW_MS, nowMs = Date.now()) {
  const token = readUserAccessToken(storage);
  if (!token) return true;
  return isAccessTokenExpired(token, null, skewMs, nowMs);
}

export function shouldAttachUserAccessToken(token, skewMs = TOKEN_SKEW_MS, nowMs = Date.now()) {
  return Boolean(token) && !isAccessTokenExpired(token, null, skewMs, nowMs);
}

export function clearExpiredUserSession(storage = localStorage, skewMs = TOKEN_SKEW_MS, nowMs = Date.now()) {
  const raw = storage.getItem(USER_ACCOUNT_KEY);
  if (!raw) return false;
  const token = readUserAccessToken(storage);
  if (token && !isAccessTokenExpired(token, null, skewMs, nowMs)) return false;
  storage.removeItem(USER_ACCOUNT_KEY);
  return true;
}

export function isLoginRoute(pathname) {
  if (!pathname) return true;
  return pathname === "/" || pathname === "";
}

export function getRequestBodyText(data) {
  if (data == null) return "";
  if (typeof data === "string") return data;
  try {
    return JSON.stringify(data);
  } catch (err) {
    return "";
  }
}

export function isGetClientTokenRequest(config) {
  return getRequestBodyText(config && config.data).includes("getClientToken");
}

export function getGraphQlAuthErrorMessage(response) {
  const errors = response && response.data && Array.isArray(response.data.errors)
    ? response.data.errors
    : [];
  const match = errors.find((error) =>
    GRAPHQL_NOT_AUTHORISED_RE.test(String(error && error.message ? error.message : ""))
  );
  return match && match.message ? String(match.message) : "";
}

export function isGraphQlNotAuthorised(response) {
  return Boolean(getGraphQlAuthErrorMessage(response));
}

export function createGraphQlUnauthorisedError(message, response, { sessionLogoutHandled = false } = {}) {
  return Object.assign(new Error(message), {
    response,
    authLogoutHandled: sessionLogoutHandled
  });
}

export function isSessionLogoutError(error) {
  return Boolean(error && error.authLogoutHandled);
}

export function resolveUnauthorisedAction({
  alreadyRetried = false,
  isClientTokenRequest = false,
  loginPage = false,
  userSessionPresent = false,
  userSessionExpired = true
} = {}) {
  if (isClientTokenRequest) return "fail_soft";
  // Login must not logout. Leftover expired AccountInfo is stripped from headers
  // (and cleared on `/`) before retry so a stale user Bearer cannot poison login.
  if (loginPage || !userSessionPresent) {
    return alreadyRetried ? "fail_soft" : "refresh_client_and_retry";
  }
  if (userSessionExpired) return "logout";
  return alreadyRetried ? "fail_soft" : "refresh_client_and_retry";
}

export {
  CLIENT_TOKEN_KEY,
  CLIENT_TOKEN_EXP_KEY,
  USER_ACCOUNT_KEY,
  TOKEN_SKEW_MS
};
